import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { OpsType, User } from "../types";
import { loginWithJWT, logoutJWT } from "./auth-client";

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isGestor: () => boolean;
  hasOpsAccess: (ops: OpsType) => boolean;
  hasScreenAccess: (screen: string, ops?: OpsType) => boolean;
  hasPermission: (key: string, ops?: OpsType) => boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  logout: () => void;
  complete2FASetup: (user: User) => void;
  complete2FALogin: (user: User) => void;
}

type LoginResult = {
  success: boolean;
  requires2FASetup?: boolean;
  requiresTwoFactor?: boolean;
  tempToken?: string;
  user?: User;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for stored auth data on mount
    const token = localStorage.getItem("auth_token");
    const userData = localStorage.getItem("user_data");

    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        // Handle both string and array formats for opsAssociations
        if (parsedUser.opsAssociations) {
          if (typeof parsedUser.opsAssociations === "string") {
            parsedUser.opsAssociations = parsedUser.opsAssociations
              .split(",")
              .map((a: string) => a.trim())
              .filter((a: string) => a); // Remove empty strings
          } else if (!Array.isArray(parsedUser.opsAssociations)) {
            // If it's neither string nor array, make it an empty array
            parsedUser.opsAssociations = [];
          }
        } else {
          parsedUser.opsAssociations = [];
        }

        if (!parsedUser.opsAssociations.includes("gestor")) {
          localStorage.removeItem("auth_token");
          localStorage.removeItem("user_data");
        } else {
          setUser(parsedUser);
        }
      } catch (error) {
        console.error("Error parsing stored user data:", error);
        localStorage.removeItem("auth_token");
        localStorage.removeItem("user_data");
      }
    }

    setIsLoading(false);
  }, []);

  const isAuthenticated = !!user && !!localStorage.getItem("auth_token");

  const login = async (
    email: string,
    password: string
  ): Promise<LoginResult> => {
    try {
      const data = await loginWithJWT(email, password);

      // Check if 2FA setup is required
      if (data.requires2FASetup) {
        return {
          success: true,
          requires2FASetup: true,
          tempToken: data.tempToken,
        };
      }

      // Check if 2FA verification is required
      if (data.requiresTwoFactor) {
        return {
          success: true,
          requiresTwoFactor: true,
          tempToken: data.tempToken,
        };
      }

      // Normal login without 2FA (shouldn't happen in production)
      if (data.user && data.token) {
        setUser(data.user);
        return {
          success: true,
          user: data.user,
        };
      }

      throw new Error("Invalid login response");
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    }
  };

  const complete2FASetup = (user: User): void => {
    setUser(user);
  };

  const complete2FALogin = (user: User): void => {
    setUser(user);
  };

  const logout = (): void => {
    logoutJWT();
    setUser(null);
  };

  const hasOpsAccess = (ops: OpsType): boolean => {
    if (!user) {
      return false;
    }
    return user.opsAssociations.includes(ops);
  };

  // Real RBAC check: does the user hold `key` (for `ops`, or any ops if omitted)?
  const hasPermission = (key: string, ops?: OpsType): boolean => {
    if (!user) {
      return false;
    }
    const groups = user.permissions || [];
    if (ops) {
      const group = groups.find((p) => p.ops === ops);
      return group ? group.keys.includes(key) : false;
    }
    return groups.some((p) => p.keys.includes(key));
  };

  // Vestigial in the admin app: no route uses requireScreen, and screen-level gating
  // for the client app is server-driven (clientes-backend /opsConfig effective screens).
  // Kept for API compatibility — an admin can see screens for any ops they belong to.
  const hasScreenAccess = (_screen: string, ops?: OpsType): boolean => {
    if (!user) {
      return false;
    }
    return ops ? hasOpsAccess(ops) : user.opsAssociations.length > 0;
  };

  const isGestor = (): boolean => {
    if (!user) {
      return false;
    }
    return user.opsAssociations && user.opsAssociations.includes("gestor");
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    isGestor,
    hasOpsAccess,
    hasScreenAccess,
    hasPermission,
    login,
    logout,
    complete2FASetup,
    complete2FALogin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
